import {
	ActionIcon,
	Avatar,
	Box,
	Group,
	Menu,
	Stack,
	Tooltip,
	useMantineTheme,
} from '@mantine/core';
import type { CustomPropertyOut } from '@repo/api-codegen/v2';
import RichTooltip from '@repo/common/components/RichTooltip/RichTooltip';
import { EntityType } from '@repo/common/enums/entityType';
import { sentenceCase } from '@repo/common/utils';
import { getEntityTypeIcon } from '@repo/common/utils/entityDisplayUtils';
import { Button, Icon, Text } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import IntegrationLogo from '../../components/IntegrationLogo';
import { SecodaMinimalLogo } from '../../components/SecodaMinimalLogo';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';
import { PopoverPreview } from '../../pages/AiChatsPage/components/PopoverPreview';
import {
	CUSTOM_PROPERTY_TYPE_MAP,
	CUSTOM_PROPERTY_VISIBILITY_ENTITY_TYPES,
} from './constants';
import type {
	CustomPropertiesSettingsColumnsProps,
	CustomPropertyWithBase,
} from './types';

export function useCustomPropertiesSettingsColumns({
	onDelete,
	onModify,
	questionsEnabled,
}: CustomPropertiesSettingsColumnsProps): ExtendedDataTableColumn<CustomPropertyWithBase>[] {
	const theme = useMantineTheme();

	const renderNameColumn = useCallback(
		(record: CustomPropertyOut) => (
			<Tooltip label={record.name} position="top" withArrow withinPortal>
				<Text size="sm" color="text/primary/default" truncate>
					{record.name}
				</Text>
			</Tooltip>
		),
		[]
	);

	const renderTypeColumn = useCallback(
		(record: CustomPropertyOut) => (
			<Text size="sm" color="text/primary/default">
				{CUSTOM_PROPERTY_TYPE_MAP[
					record.value_type as keyof typeof CUSTOM_PROPERTY_TYPE_MAP
				] || record.value_type}
			</Text>
		),
		[]
	);

	const renderIntegrationColumn = useCallback(
		(record: CustomPropertyOut) => {
			const iconSize = theme.other.iconSize.sm;
			return (
				<Group spacing="3xs" noWrap>
					{record.integration?.name && record.integration?.id ? (
						<>
							<RichTooltip
								maxWidth={theme.other.space[60]}
								title={`Custom Property comes from ${record.integration.name}`}
								body={`This custom property is synced from ${record.integration.name}.`}
								linkLabel={`${record.integration.name} settings`}
								linkUrl={`/integrations/${record.integration.id}`}
							>
								<Box>
									<IntegrationLogo
										integrationId={record.integration?.id}
										integrationType={record.integration?.type}
										width={iconSize}
										height={iconSize}
									/>
								</Box>
							</RichTooltip>
							<Text size="sm" color="text/primary/default" lineClamp={1}>
								{record.integration?.name}
							</Text>
						</>
					) : (
						<Tooltip label="Secoda" position="right">
							<Group spacing="3xs" noWrap>
								<SecodaMinimalLogo width={iconSize} height={iconSize} />
								<Text size="sm" color="text/primary/default" pl="2xs">
									Secoda
								</Text>
							</Group>
						</Tooltip>
					)}
				</Group>
			);
		},
		[theme]
	);

	const renderEntityTypesColumn = useCallback(
		(record: CustomPropertyOut) => {
			const entityTypes = record.entity_types || [];

			const filteredEntityTypes =
				CUSTOM_PROPERTY_VISIBILITY_ENTITY_TYPES.filter(
					(entityType) => entityType !== EntityType.question || questionsEnabled
				);

			const hasAllEntityType = entityTypes.includes(EntityType.all);
			const entityTypesToDisplay = hasAllEntityType
				? filteredEntityTypes
				: entityTypes;

			const visibleTypes = entityTypesToDisplay.slice(0, 3);
			const totalCount = hasAllEntityType
				? filteredEntityTypes.length
				: entityTypes.length;

			const previewIcons = (
				<Group spacing="xs" noWrap>
					{visibleTypes.length > 0 ? (
						<Avatar.Group spacing="xs">
							{visibleTypes.map((entityType) => (
								<Avatar
									key={`${record.id}-${entityType}`}
									size="xs"
									bg={theme.other.getColor('fill/secondary/default')}
									sx={{
										border: `1px solid ${theme.other.getColor('border/transparent/default')}`,
										borderRadius: theme.other.borderRadius.xs,
									}}
								>
									{getEntityTypeIcon(entityType as EntityType)}
								</Avatar>
							))}
						</Avatar.Group>
					) : (
						<Button
							leftIconName="plus"
							variant="tertiary"
							size="md"
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation();
								onModify(record);
							}}
							styles={{
								root: {
									color: theme.other.getColor('text/secondary/default'),
								},
							}}
						>
							Add Resource Type Visibility
						</Button>
					)}
					{totalCount > 0 && (
						<Text size="sm" color="text/primary/default">
							{totalCount} resource{totalCount !== 1 ? 's' : ''}
						</Text>
					)}
				</Group>
			);

			const dropdownContent = (
				<Stack spacing="xs">
					<Text size="xs" color="text/primary/default" weight="semibold">
						Resource Type Visibility
					</Text>
					<Box>
						<Stack spacing="xs">
							{entityTypesToDisplay.map((entity, i) => {
								const uniqueKey = `${record.id}-${entity}-${i}`;
								return (
									<Group key={uniqueKey} spacing="3xs" noWrap align="center">
										<Avatar size="sm">
											{getEntityTypeIcon(entity as EntityType)}
										</Avatar>
										<Stack spacing={0}>
											<Text
												truncate
												maw={theme.other.space[60]}
												size="xs"
												color="text/secondary/default"
												weight="semibold"
												tt="capitalize"
											>
												{sentenceCase(entity.replace('_', ' '))}
											</Text>
										</Stack>
									</Group>
								);
							})}
						</Stack>
					</Box>
				</Stack>
			);

			return (
				<PopoverPreview
					preview={previewIcons}
					dropdown={dropdownContent}
					width={theme.other.space[40]}
					isInteractive={totalCount > 0}
				/>
			);
		},
		[theme, questionsEnabled]
	);

	const renderActionsColumn = useCallback(
		(record: CustomPropertyOut) => {
			const canDelete =
				!(record as CustomPropertyOut & { default?: boolean }).default &&
				!record.integration;

			return (
				<Box aria-label="row-actions">
					<Menu withinPortal>
						<Menu.Target>
							<ActionIcon
								size="sm"
								onClick={(e: React.MouseEvent) => e.stopPropagation()}
							>
								<Icon name="dots" color="icon/secondary/default" />
							</ActionIcon>
						</Menu.Target>
						<Menu.Dropdown>
							<Menu.Item
								icon={<Icon name="pencil" />}
								onClick={(e: React.MouseEvent) => {
									e.stopPropagation();
									onModify(record);
								}}
							>
								Edit
							</Menu.Item>

							{canDelete && (
								<Menu.Item
									icon={<Icon name="trash" />}
									onClick={(e: React.MouseEvent) => {
										e.stopPropagation();
										onDelete(record);
									}}
								>
									Delete...
								</Menu.Item>
							)}
						</Menu.Dropdown>
					</Menu>
				</Box>
			);
		},
		[onDelete, onModify]
	);

	const columns = useMemo(
		() =>
			[
				{
					accessor: 'name',
					title: 'Name',
					titleLabel: 'Name',
					render: renderNameColumn,
					width: 150,
				},
				{
					accessor: 'value_type',
					title: 'Type',
					titleLabel: 'Type',
					render: renderTypeColumn,
					width: 90,
				},
				{
					accessor: 'integration',
					title: 'Source',
					titleLabel: 'Source',
					render: renderIntegrationColumn,
					width: 130,
				},
				{
					accessor: 'entity_types',
					title: 'Resource Type Visibility',
					titleLabel: 'Resource Type Visibility',
					render: renderEntityTypesColumn,
					width: 220,
				},
				{
					accessor: '',
					title: '',
					titleLabel: '',
					render: renderActionsColumn,
					width: 60,
				},
			] as ExtendedDataTableColumn<CustomPropertyWithBase>[],
		[
			renderNameColumn,
			renderTypeColumn,
			renderIntegrationColumn,
			renderEntityTypesColumn,
			renderActionsColumn,
		]
	);

	return columns;
}
